import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VDTabsEditor = () => {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [currentTabId, setCurrentTabId] = useState(null);

  const LANGS = ["En", "Ar", "Ku"];

  const resolveUrl = (url) =>
    url?.startsWith("http") ? url : `${API_BASE}${url}`;

  // ============================================================
  // LOAD (3 LANGUAGES)
  // ============================================================
  useEffect(() => {
    const load = async () => {
      try {
        const [enRes, arRes, kuRes] = await Promise.all([
          axios.get(`${API_BASE}/api/VDTabsSection?lang=en`),
          axios.get(`${API_BASE}/api/VDTabsSection?lang=ar`),
          axios.get(`${API_BASE}/api/VDTabsSection?lang=ku`),
        ]);

        // EN = structure baseline, fill AR/KU text
        const en = enRes.data;
        const ar = arRes.data;
        const ku = kuRes.data;

        const merged = en.map((tab, i) => ({
          id: tab.id,
          image: tab.image,

          TitleEn: tab.title,
          TitleAr: ar[i].title,
          TitleKu: ku[i].title,

          DescEn: tab.desc,
          DescAr: ar[i].desc,
          DescKu: ku[i].desc,
        }));

        setTabs(merged);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load VDTabs:", err);
      }
    };

    load();
  }, []);

  // Handle change in fields
  const handleChange = (tabId, field, value) => {
    setTabs((prev) =>
      prev.map((t) => (t.id === tabId ? { ...t, [field]: value } : t))
    );
  };

  // ============================================================
  // SAVE TEXTS
  // ============================================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    const dto = {
      Tabs: tabs.map((t) => ({
        TabId: t.id,
        TitleEn: t.TitleEn,
        TitleAr: t.TitleAr,
        TitleKu: t.TitleKu,
        DescEn: t.DescEn,
        DescAr: t.DescAr,
        DescKu: t.DescKu,
      })),
    };

    try {
      await axios.put(`${API_BASE}/api/VDTabsSection`, dto, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Tabs updated successfully");
    } catch (err) {
      console.error(err);
      alert("Update failed");
    }
  };

  // ============================================================
  // UPLOAD IMAGE
  // ============================================================
  const uploadImage = async () => {
    if (!uploadFile) return alert("Please select a file.");

    const token = localStorage.getItem("token");

    const fd = new FormData();
    fd.append("TabId", currentTabId);
    fd.append("Image", uploadFile);

    try {
      await axios.post(`${API_BASE}/api/VDTabsSection/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Image updated!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  if (loading) return <p>Loading...</p>;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">VOYAH DREAM – Tabs Section Editor</h2>

      <div className="row">
        {tabs.map((tab) => (
          <div key={tab.id} className="col-md-6 mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h4 className="fw-bold mb-3">Tab {tab.id}</h4>

                {/* LANG GROUPS */}
                <div className="row">
                  {LANGS.map((lang) => (
                    <div key={lang} className="col-12 mb-3">
                      <h6 className="text-primary">{lang}</h6>

                      {/* TITLE */}
                      <label className="form-label">Title</label>
                      <input
                        className="form-control mb-2"
                        value={tab[`Title${lang}`]}
                        onChange={(e) =>
                          handleChange(tab.id, `Title${lang}`, e.target.value)
                        }
                      />

                      {/* DESCRIPTION */}
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={tab[`Desc${lang}`]}
                        onChange={(e) =>
                          handleChange(tab.id, `Desc${lang}`, e.target.value)
                        }
                      />
                    </div>
                  ))}
                </div>

                {/* IMAGE BLOCK */}
                <h6 className="fw-bold mt-4">Image</h6>
                {tab.image ? (
                  <img
                    src={resolveUrl(tab.image)}
                    alt=""
                    className="rounded w-100 mb-2"
                    style={{ maxHeight: "230px", objectFit: "cover" }}
                  />
                ) : (
                  <p>No image uploaded.</p>
                )}

                <button
                  className="btn btn-primary mt-2"
                  onClick={() => {
                    setCurrentTabId(tab.id);
                    setUploadFile(null);
                    setShowModal(true);
                  }}
                >
                  Upload / Replace Image
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAVE ALL BUTTON */}
      <div className="text-end">
        <button className="btn btn-success px-4" onClick={handleSave}>
          Save All Changes
        </button>
      </div>

      {/* ============================================================
          MODAL UPLOAD
      ============================================================ */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Tab Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-primary" onClick={uploadImage}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VDTabsEditor;
